import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

const TablePredictorLeaderboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const leagueCode = searchParams.get('leagueCode');

  const [leagueData, setLeagueData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [teamsData, setTeamsData] = useState([]);
  const [predictionsData, setPredictionsData] = useState([]);
  const [currentGameweek, setCurrentGameweek] = useState(0);

  const calculateScore = useCallback((userId, userPredictions) => {
    if (!userPredictions) return 0;

    const processedTeams = teamsData.map((team, index) => ({
      ...team,
      position: index + 1,
      inputPosition: index + 1
    }));

    const predictedTeams = Object.keys(userPredictions)
      .sort()
      .map(key => userPredictions[key])
      .map((name, index) => {
        const team = processedTeams.find(t => t.name === name);
        return { ...team, position: index + 1, inputPosition: index + 1 };
      });

    const actualTeams = predictionsData.map(team => ({
      ...team,
      actualPosition: team.position
    }));

    const combinedTeams = predictedTeams.map(predictedTeam => {
      const actualTeam = actualTeams.find(team => team.teamId === predictedTeam.id);
      const difference = predictedTeam.inputPosition - actualTeam.actualPosition;
      const score = 20 - Math.abs(difference);
      return { ...predictedTeam, score };
    });

    return combinedTeams.reduce((acc, team) => acc + team.score, 0);
  }, [teamsData, predictionsData]);

  const fetchLeaderboardData = useCallback(async (members) => {
    try {
      const usersData = await Promise.all(
        members.map(async (userId) => {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userPredictionDoc = await getDoc(doc(db, 'table_predictions', `${userId}_${currentSeason}`));
            const userPredictionsTemp = userPredictionDoc.exists() ? userPredictionDoc.data() : null;

            // Keep only key pairs where key begins with 'position_'
            const userPredictions = userPredictionsTemp
              ? Object.keys(userPredictionsTemp)
                  .filter(key => key.startsWith('position_'))
                  .reduce((obj, key) => {
                    obj[key] = userPredictionsTemp[key];
                    return obj;
                  }, {})
              : null;

            const score = calculateScore(userId, userPredictions);

            return {
              id: userId,
              name: userData.display_name || 'Unknown User',
              score: score
            };
          }
          return null;
        })
      );

      const validUsersData = usersData.filter(user => user !== null);
      return validUsersData.sort((a, b) => b.score - a.score);
    } catch (error) {
      console.error("Error fetching leaderboard data:", error);
      return [];
    }
  }, [calculateScore, currentSeason]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const seasonResponse = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`);
        const currentGameweekResponse = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`);
        const newCurrentSeason = seasonResponse.data.season;
        setCurrentSeason(newCurrentSeason);
        setCurrentGameweek(currentGameweekResponse.data.gameweek);
      } catch (error) {
        console.error("Error fetching current season:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchTeamsAndPredictions = async () => {
      try {
        const [teamsResponse, predictionsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/teams`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/table-predictions/live-table`)
        ]);

        setTeamsData(teamsResponse.data);
        setPredictionsData(predictionsResponse.data);
      } catch (error) {
        console.error("Error fetching teams and predictions data:", error);
      }
    };

    fetchTeamsAndPredictions();
  }, []);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      try {
        const leagueSnapshot = await getDocs(query(collection(db, 'leagues'), where('leagueCode', '==', leagueCode), where('type', '==', 'table')));

        if (leagueSnapshot.empty) {
          navigate('/not-found', { replace: true });
          return;
        }

        const leagueDoc = leagueSnapshot.docs[0];
        const leagueData = leagueDoc.data();
        setLeagueData(leagueData);

        const leaderboardData = await fetchLeaderboardData(leagueData.members);
        setLeaderboardData(leaderboardData);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
        navigate('/not-found', { replace: true });
      }
    };

    if (currentSeason && teamsData.length > 0 && predictionsData.length > 0) {
      fetchLeaderboard();
    }
  }, [leagueCode, navigate, fetchLeaderboardData, currentSeason, teamsData, predictionsData]);

  const getPerformanceLink = (userId) => {
    return `/performance?uid=${userId}&openOnTab=tablePredictor`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
      </div>
    );
  }

  if (currentGameweek === 0) {
    // Set all user scores to 0
    leaderboardData.forEach(user => user.score = 0);
  } 

  const tableVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-4xl"
    >
      <h1 className="text-4xl font-bold text-premier-purple mb-2 text-center">Table Predictor Leaderboard</h1>
      <h2 className="text-3xl font-semibold text-premier-pink mb-4 text-center">{leagueData.name}</h2>
      
      <div className="flex justify-center items-center space-x-4 mb-6 text-gray-600">
        <p className="text-sm font-medium">League Code: <span className="font-bold text-premier-purple">{leagueCode}</span></p>
      </div>

      <motion.div
        variants={tableVariants}
        initial="hidden"
        animate="visible"
        className="bg-white shadow-lg rounded-lg overflow-hidden"
      >
        <table className="w-full">
          <thead className="bg-premier-purple text-white">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Rank</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Score</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leaderboardData.map((user, index) => (
              <motion.tr
                key={user.id}
                custom={index}
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Link to={getPerformanceLink(user.id)} className="text-premier-purple hover:underline">
                    {user.name}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.score}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>
        <p className="mt-2 text-gray-700 text-center mb-6">Visit the <Link to="/scoring-guide?openRulesOn=table_leagues" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for scoring details.</p>
      <div className="flex justify-center mt-6">
        <Link to="/leagues?selectedType=table" className="px-3 py-2 rounded-md text-sm font-medium bg-premier-purple text-white hover:bg-premier-pink hover:text-white">
          Return to Table Predictor Leagues
        </Link>
      </div>
    </motion.div>
  );
};

export default TablePredictorLeaderboard;
